import { supabase } from '../lib/supabase';

/**
 * Blog Service - Handles all blog article operations
 * Converts between database snake_case and React camelCase
 */
export const blogService = {
  /**
   * Get all published blog articles with optional filtering
   * @param {Object} options - Query options
   * @param {string} options.category - Filter by category
   * @param {boolean} options.featuredOnly - Get only featured articles
   * @param {number} options.limit - Limit results
   * @returns {Promise<Array>} Array of blog articles in camelCase
   */
  async getAll(options = {}) {
    try {
      let query = supabase?.from('blog_articles')?.select('*')?.eq('is_published', true)?.order('published_date', { ascending: false });

      // Apply filters
      if (options?.category) {
        query = query?.eq('category', options?.category);
      }

      if (options?.featuredOnly) {
        query = query?.eq('is_featured', true);
      }

      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert to camelCase
      return data?.map(article => this._toCamelCase(article)) || [];
    } catch (error) {
      console.error('Error fetching blog articles:', error);
      throw error;
    }
  },

  /**
   * Get a single article by slug
   * @param {string} slug - Article slug
   * @returns {Promise<Object>} Article in camelCase
   */
  async getBySlug(slug) {
    try {
      const { data, error } = await supabase?.from('blog_articles')?.select('*')?.eq('slug', slug)?.eq('is_published', true)?.single();

      if (error) throw error;

      // Increment view count
      await this._incrementViewCount(data?.id);

      return this._toCamelCase(data);
    } catch (error) {
      console.error('Error fetching article by slug:', error);
      throw error;
    }
  },

  /**
   * Get featured articles
   * @param {number} limit - Number of featured articles to return
   * @returns {Promise<Array>} Array of featured articles
   */
  async getFeatured(limit = 3) {
    return this.getAll({ featuredOnly: true, limit });
  },

  /**
   * Get recent articles
   * @param {number} limit - Number of recent articles
   * @returns {Promise<Array>} Array of recent articles
   */
  async getRecent(limit = 5) {
    return this.getAll({ limit });
  },

  /**
   * Get articles by category
   * @param {string} category - Category to filter by
   * @returns {Promise<Array>} Array of articles in category
   */
  async getByCategory(category) {
    return this.getAll({ category });
  },

  /**
   * Search articles by title or content
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Array of matching articles
   */
  async search(searchTerm) {
    try {
      const { data, error } = await supabase?.from('blog_articles')?.select('*')?.eq('is_published', true)?.or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%,excerpt.ilike.%${searchTerm}%`)?.order('published_date', { ascending: false });

      if (error) throw error;

      return data?.map(article => this._toCamelCase(article)) || [];
    } catch (error) {
      console.error('Error searching articles:', error);
      throw error;
    }
  },

  /**
   * Get article count by category
   * @returns {Promise<Object>} Category counts
   */
  async getCategoryCounts() {
    try {
      const { data, error } = await supabase?.from('blog_articles')?.select('category')?.eq('is_published', true);

      if (error) throw error;

      // Count articles per category
      const counts = {
        bail_process: 0,
        legal_rights: 0,
        family_support: 0,
        sacramento_news: 0
      };

      data?.forEach(article => {
        if (counts?.hasOwnProperty(article?.category)) {
          counts[article.category]++;
        }
      });

      return counts;
    } catch (error) {
      console.error('Error getting category counts:', error);
      throw error;
    }
  },

  /**
   * Increment article view count
   * @private
   * @param {string} articleId - Article ID
   */
  async _incrementViewCount(articleId) {
    try {
      const { error } = await supabase?.rpc('increment', {
        row_id: articleId,
        table_name: 'blog_articles',
        column_name: 'view_count'
      });

      if (error) {
        // If RPC doesn't exist, fallback to manual increment
        const { data: article } = await supabase?.from('blog_articles')?.select('view_count')?.eq('id', articleId)?.single();

        if (article) {
          await supabase?.from('blog_articles')?.update({ view_count: (article?.view_count || 0) + 1 })?.eq('id', articleId);
        }
      }
    } catch (error) {
      // Silently fail - view count is not critical
      console.warn('Failed to increment view count:', error);
    }
  },

  /**
   * Convert database snake_case to React camelCase
   * @private
   * @param {Object} article - Article from database
   * @returns {Object} Article in camelCase
   */
  _toCamelCase(article) {
    if (!article) return null;

    return {
      id: article?.id,
      title: article?.title,
      slug: article?.slug,
      excerpt: article?.excerpt,
      content: article?.content,
      category: article?.category,
      featuredImageUrl: article?.featured_image_url,
      authorName: article?.author_name,
      readingTimeMinutes: article?.reading_time_minutes,
      isFeatured: article?.is_featured,
      isPublished: article?.is_published,
      publishedDate: article?.published_date,
      createdAt: article?.created_at,
      updatedAt: article?.updated_at,
      viewCount: article?.view_count,
      metaDescription: article?.meta_description,
      metaKeywords: article?.meta_keywords
    };
  }
};

/**
 * Category display names
 */
export const BLOG_CATEGORIES = {
  bail_process: 'Bail Process',
  legal_rights: 'Legal Rights',
  family_support: 'Family Support',
  sacramento_news: 'Sacramento News'
};

/**
 * Get friendly category name
 * @param {string} category - Category key
 * @returns {string} Display name
 */
export const getCategoryDisplayName = (category) => {
  return BLOG_CATEGORIES?.[category] || category;
};
function fetchArticles(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: fetchArticles is not implemented yet.', args);
  return null;
}

export { fetchArticles };
function fetchFeaturedArticles(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: fetchFeaturedArticles is not implemented yet.', args);
  return null;
}

export { fetchFeaturedArticles };
function fetchCategories(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: fetchCategories is not implemented yet.', args);
  return null;
}

export { fetchCategories };